/**
 * Часы разговора: сколько времени идёт звонок.
 *
 * <p>До этого экран звонка показывал только слово «Разговор», и понять, минуту
 * говорят или сорок, было нельзя ни на телефоне, ни в вебе. Отсчёт ведётся от
 * момента, когда звонок стал разговором, а не от открытия экрана: гудки в
 * длительность не входят — так же, как в аналитике (`call_ended.duration_bucket`).
 */
import { useEffect, useRef, useState } from 'react';

/**
 * `мм:сс`, а после часа — `ч:мм:сс`.
 *
 * <p>Минуты и секунды всегда двузначные: на моноширинных цифрах строка тогда не
 * дёргается по ширине каждую секунду. Отрицательное и NaN сводятся к нулю —
 * часы устройств расходятся чаще, чем кажется.
 */
export function formatCallDuration(totalSeconds: number): string {
  const total = Number.isFinite(totalSeconds) ? Math.max(0, Math.floor(totalSeconds)) : 0;
  const hours = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const mmss = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  return hours > 0 ? `${hours}:${mmss}` : mmss;
}

/**
 * Сколько секунд прошло — по разнице меток, а не счётчиком тиков.
 *
 * <p>Свёрнутое приложение и погашенный экран таймеры не будят: счётчик «плюс
 * один за тик» после возвращения показал бы меньше, чем длился разговор.
 */
export function callElapsedSeconds(startedAt: number, now: number): number {
  return Math.max(0, Math.floor((now - startedAt) / 1000));
}

/**
 * Задержка до следующего тика — ровно до границы секунды, чтобы цифра менялась
 * тогда же, когда меняется секунда, а не через случайную долю после неё.
 */
export function nextCallTickDelayMs(elapsedMs: number): number {
  const rest = ((elapsedMs % 1000) + 1000) % 1000;
  return 1000 - rest;
}

/** Секунды с начала разговора; при `active === false` отсчёт сброшен в ноль. */
export function useCallDuration(active: boolean): number {
  const [seconds, setSeconds] = useState(0);
  const startedAtRef = useRef<number | null>(null);
  // Ноль при окончании разговора ставим в рендере, а не в эффекте: так следующий
  // звонок не покажет на первом кадре хвост предыдущего и не будет лишнего прохода.
  const [wasActive, setWasActive] = useState(active);
  if (wasActive !== active) {
    setWasActive(active);
    if (!active) setSeconds(0);
  }

  useEffect(() => {
    if (!active) {
      startedAtRef.current = null;
      return;
    }
    const startedAt = startedAtRef.current ?? Date.now();
    startedAtRef.current = startedAt;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = () => {
      const now = Date.now();
      setSeconds(callElapsedSeconds(startedAt, now));
      timer = setTimeout(tick, nextCallTickDelayMs(now - startedAt));
    };
    tick();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [active]);

  return seconds;
}

/**
 * Секунды с заданной метки начала разговора; `null` — разговора нет (ноль).
 *
 * <p>Нужен, с тех пор как звонок можно свернуть: экран звонка и плашка «Вернуться к звонку»
 * монтируются заново посреди разговора, и счётчик «от монтирования» начинал бы с нуля. Метку
 * держит сама сессия звонка — от момента, когда взяли трубку.
 */
export function useCallDurationSince(startedAt: number | null): number {
  // «Сейчас» — состоянием, которое обновляет таймер; секунды считаются из него в рендере.
  // Начальное значение — момент монтирования: экран, открытый посреди разговора, сразу
  // показывает настоящую длительность, а не ноль до первого тика.
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (startedAt == null) return undefined;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = () => {
      const t = Date.now();
      setNow(t);
      timer = setTimeout(tick, nextCallTickDelayMs(t - startedAt));
    };
    timer = setTimeout(tick, 0);
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [startedAt]);

  return startedAt == null ? 0 : callElapsedSeconds(startedAt, now);
}
